import { addDays, areIntervalsOverlapping, isEqual } from 'date-fns'
import { getNestedProperty, dateAdjustedForTimezone } from '@opallabs/lambda'

// Overlap rather than containment: multi-week moments starting before the
// visible week still index so Content mode can render their in-week posts.
const momentOverlapsWeek = (moment, startOfWeek, endOfWeek) => {
  const start = dateAdjustedForTimezone(moment.scheduled_at)
  if (!start) return false
  const end = moment.scheduled_end_at
    ? dateAdjustedForTimezone(moment.scheduled_end_at)
    : start
  return areIntervalsOverlapping(
    { start, end },
    { start: startOfWeek, end: endOfWeek },
    { inclusive: true }
  )
}

const isMomentInWorkspace = (moment, topLevelState) => {
  const currentWorkspace = topLevelState.current_workspace_id?.toString()
  if (!currentWorkspace) return false
  if (moment.relationships?.brand?.id === currentWorkspace) return true
  const storyRefs = moment.relationships?.stories
  if (!Array.isArray(storyRefs)) return false
  return storyRefs.some((ref) => {
    const story = topLevelState.jsonApiEntities.story?.[ref.id]
    if (!story?.relationships?.brands) return false
    return story.relationships.brands.some((b) => b.id === currentWorkspace)
  })
}

// Overlap check mirroring `momentOverlapsWeek` — content is considered in
// the visible week when its [scheduled_at, takedown_scheduled_at] window
// intersects [startOfWeek, endOfWeek]. Matches the server's
// `include_overlapping_schedules` filter so a post scheduled before the
// week but taking down inside it still counts as in-window.
const contentOverlapsWeek = (content, startOfWeek, endOfWeek) => {
  const start = dateAdjustedForTimezone(content.scheduled_at)
  if (!start) return false
  const end = content.takedown_scheduled_at
    ? dateAdjustedForTimezone(content.takedown_scheduled_at)
    : start
  return areIntervalsOverlapping(
    { start, end },
    { start: startOfWeek, end: endOfWeek },
    { inclusive: true }
  )
}

// Mirrors the moment title rule of the server's search
// (`Moments::ElasticsearchMomentSearch`): every search word must start a
// title word, or, for a non-ASCII search, equal one.
const NON_WORD_CHARACTERS = /[^\p{L}\p{M}\p{N}\p{Pc}']+/u
const NON_ASCII = /[^\p{ASCII}\p{P}]/u
const words = (text) =>
  text.toLowerCase().split(NON_WORD_CHARACTERS).filter(Boolean)

const titleMatchesSearch = (title, searchTerm) => {
  if (!title) return false
  const titleWords = words(title)
  const matches = NON_ASCII.test(searchTerm)
    ? (term) => titleWords.includes(term)
    : (term) => titleWords.some((word) => word.startsWith(term))
  return words(searchTerm).every(matches)
}

const reducer = (state = {}, action) => {
  switch (action.type) {
    case 'DESERIALIZER_INDEXING': {
      const newIndices = action.payload.payloadIndices
      if (newIndices.type !== 'moment' && newIndices.type !== 'content') {
        return state
      }

      const topLevelState = action.payload.topLevelState
      let momentIndices = state.moments || {}
      let contentIndices = state.content || {}

      const startOfWeek = topLevelState.weekViewStartDay
      // here we add 7 days instead of 6, which means we are looking
      // at a total range of 8 days when indexing. This is needed
      // to ensure that all timezones see the expected moments
      // in their week.
      const endOfWeek = addDays(topLevelState.weekViewStartDay, 7)

      const originalMomentStartDay = getNestedProperty(
        state,
        'meta',
        'momentStartDay'
      )
      const originalContentStartDay = getNestedProperty(
        state,
        'meta',
        'contentStartDay'
      )
      const originalSearchTerm = getNestedProperty(state, 'meta', 'searchTerm')
      // Older persisted state predates `contentSearchTerm`; fall back to the
      // plain search term so an unchanged-search content page appends instead
      // of wrongly clearing the week.
      const persistedContentSearchTerm = getNestedProperty(
        state,
        'meta',
        'contentSearchTerm'
      )
      const originalContentSearchTerm =
        persistedContentSearchTerm === undefined
          ? originalSearchTerm
          : persistedContentSearchTerm

      const momentStartDayChanged = !isEqual(
        startOfWeek,
        originalMomentStartDay
      )
      const contentStartDayChanged = !isEqual(
        startOfWeek,
        originalContentStartDay
      )
      const searchHasChanged = originalSearchTerm !== topLevelState.searchTerm
      // The content fetch can load several pages; only the first should clear
      // the old results and the rest should append. `searchTerm` can't drive
      // that — it only updates when moments load, so every content page would
      // see the search as still changed and wipe the previous page. Content
      // keeps its own `contentSearchTerm`, which updates as soon as the first
      // page resets (the same approach `contentStartDay` uses for the week).
      const contentSearchHasChanged =
        originalContentSearchTerm !== topLevelState.searchTerm

      const momentsShouldReset =
        newIndices.type === 'moment' &&
        (momentStartDayChanged || searchHasChanged)
      const contentShouldReset =
        newIndices.type === 'content' &&
        (contentStartDayChanged || contentSearchHasChanged)

      if (momentsShouldReset) {
        momentIndices = {}
      }

      let contentStartDay = originalContentStartDay
      let contentSearchTerm = originalContentSearchTerm
      if (contentShouldReset) {
        contentIndices = {}
        contentStartDay = startOfWeek
        contentSearchTerm = topLevelState.searchTerm
      }

      let momentStartDay = originalMomentStartDay
      if (
        newIndices.type === 'moment' &&
        topLevelState.jsonApiEntities.moment !== undefined &&
        topLevelState.weekViewStartDay !== undefined
      ) {
        momentStartDay = startOfWeek
        newIndices.ids.forEach((id) => {
          const currentMoment = topLevelState.jsonApiEntities.moment[id]

          if (
            currentMoment &&
            momentOverlapsWeek(currentMoment, startOfWeek, endOfWeek)
          ) {
            const currentWorkspace =
              topLevelState.current_workspace_id.toString()
            let momentIsInCurrentWorkspace =
              currentWorkspace === currentMoment.relationships.brand.id

            if (
              currentMoment.relationships.stories &&
              currentMoment.relationships.stories.length
            ) {
              const storiesIdsList = currentMoment.relationships.stories.map(
                (story) => story.id
              )

              storiesIdsList.forEach((storyId) => {
                const story = topLevelState.jsonApiEntities.story[storyId]
                if (story.relationships.brands) {
                  const storyWorkspaceIdsList = story.relationships.brands.map(
                    (workspace) => workspace.id
                  )

                  if (
                    storyWorkspaceIdsList.includes(
                      topLevelState.current_workspace_id.toString()
                    )
                  ) {
                    momentIsInCurrentWorkspace = true
                  }
                }
              })
            }

            // During a search, only the week view's own fetch holds the
            // server's results; the server also matches post text, so those
            // can have any title. Any other new moment, such as one from the
            // websocket, must match by title.
            const matchesActiveSearch =
              !topLevelState.searchTerm ||
              action.payload.view === 'weekView' ||
              momentIndices[id] ||
              titleMatchesSearch(currentMoment.title, topLevelState.searchTerm)

            if (momentIsInCurrentWorkspace && matchesActiveSearch) {
              momentIndices[id] = id
            }
          }
        })
      }

      if (
        newIndices.type === 'content' &&
        topLevelState.jsonApiEntities.content
      ) {
        newIndices.ids.forEach((id) => {
          const currentContent = topLevelState.jsonApiEntities.content[id]
          if (!currentContent) return
          const momentId = currentContent.relationships?.moment?.id
          const momentFromContent =
            momentId && topLevelState.jsonApiEntities.moment?.[momentId]
          if (!momentFromContent) return

          if (momentIndices[momentFromContent.id]) {
            contentIndices[id] = id
            return
          }

          // Fallback when content arrives before moments have been
          // re-indexed on week-change — otherwise the content gets dropped.
          // Bounded by the visible week so stale responses from a prior
          // week/search can't leak in via workspace membership alone.
          if (
            isMomentInWorkspace(momentFromContent, topLevelState) &&
            contentOverlapsWeek(currentContent, startOfWeek, endOfWeek)
          ) {
            contentIndices[id] = id
            return
          }

          if (currentContent.scheduled_at !== momentFromContent.scheduled_at) {
            contentIndices[id] = 'moment for content not found'
          }
        })
      }

      // Catch content that skipped indexing earlier because its parent
      // moment wasn't in the store yet. Week-bounded so content entities
      // hydrated by a prior week's fetch don't leak in.
      if (newIndices.type === 'moment' && momentsShouldReset) {
        Object.values(topLevelState.jsonApiEntities.content || {}).forEach(
          (c) => {
            if (!c?.id || contentIndices[c.id]) return
            const pmId = c.relationships?.moment?.id
            if (!pmId || !momentIndices[pmId]) return
            if (!contentOverlapsWeek(c, startOfWeek, endOfWeek)) return
            contentIndices[c.id] = c.id
          }
        )
      }

      return {
        meta: {
          // since this reducer runs once per entity type, in the event
          // a content request has finished before moments the search
          // term would get reset before parsing through moments. This
          // would cause all moments showing regardless of search term.
          searchTerm:
            newIndices.type === 'content'
              ? originalSearchTerm
              : topLevelState.searchTerm,
          weekViewStartDay: topLevelState.weekViewStartDay,
          contentStartDay,
          contentSearchTerm,
          momentStartDay,
        },
        moments: {
          ...momentIndices,
        },
        content: {
          ...contentIndices,
        },
      }
    }
    default: {
      return state
    }
  }
}

export default reducer
